import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import { useAuth } from "@bw/shared/hooks";
import type { LeaderboardEntryResponse } from "@bw/shared/types/generated";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PillChip } from "../../../common/components/PillChip";
import { visuallyHidden } from "../../../common/visuallyHidden";
import { Page } from "../../../common/components/Page";
import { LeaderboardTable } from "../components/LeaderboardTable";
import { useLeaderboard } from "../hooks/useLeaderboard";
import { FacilityScopePicker } from "../../../common/components/FacilityScopePicker";
import { ratingLabel } from "../components/competeDisplay";

/**
 * Your own standing pinned as a card above the list: the server's rank and
 * rating (three decimals), or "Unrated" before placement.
 */
function YourRankCard({ entry }: { entry: LeaderboardEntryResponse }) {
  return (
    <Box
      aria-label="Your ranking"
      role="group"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 1.5,
        py: 1.25,
        border: 1,
        borderColor: "divider",
        borderRadius: "12px",
        bgcolor: "background.paper",
      }}
    >
      <Typography sx={{ fontSize: "1.25rem", fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>
        #{entry.rank}
      </Typography>
      <Box sx={{ minWidth: 0 }}>
        <Typography noWrap sx={{ fontSize: "0.9375rem", fontWeight: 700 }}>
          You · {entry.is_placed ? `${ratingLabel(entry.rating)} pts` : "Unrated"}
        </Typography>
        <Typography noWrap variant="body2" color="text.secondary">
          {entry.games_played} {entry.games_played === 1 ? "game" : "games"} played
        </Typography>
      </Box>
    </Box>
  );
}

export function RankingsPage() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const facilityId = params.get("facility_id") || undefined;
  const requestedOffset = Number(params.get("offset") || 0);
  const offset =
    Number.isSafeInteger(requestedOffset) && requestedOffset >= 0
      ? requestedOffset
      : 0;
  const query = useLeaderboard(facilityId, 25, offset, Boolean(user));
  const page = query.data;
  const mine =
    page?.items.find((row) => user?.uid != null && row.user_id === user.uid) ??
    page?.viewer_entry ??
    null;
  const [choosingFacility, setChoosingFacility] = useState(false);
  const facilityScope = Boolean(facilityId) || choosingFacility;
  const setFacility = (nextId: string | undefined) => {
    const next = new URLSearchParams(params);
    if (nextId) next.set("facility_id", nextId);
    else next.delete("facility_id");
    next.delete("offset");
    setParams(next);
  };
  function navigate(next: number) {
    const updated = new URLSearchParams(params);
    updated.set("offset", String(next));
    setParams(updated);
  }
  return (
    <Page title="Rankings" hideTitle narrow>
      <Stack spacing={1.5}>
        <Stack role="group" aria-label="Ranking scope" direction="row" spacing={0.75}>
          <PillChip
            selected={!facilityScope}
            onClick={() => {
              setChoosingFacility(false);
              setFacility(undefined);
            }}
          >
            Everywhere
          </PillChip>
          <PillChip
            selected={facilityScope}
            onClick={() => setChoosingFacility(true)}
          >
            A facility
          </PillChip>
        </Stack>
        {facilityScope ? (
          <FacilityScopePicker facilityId={facilityId} onChange={setFacility} />
        ) : null}
        {query.isError ? (
          <Alert severity="error">
            Could not load rankings. Please try again.
          </Alert>
        ) : !page ? (
          <Typography role="status">Loading rankings…</Typography>
        ) : (
          <>
            <Typography sx={visuallyHidden}>
              {page.scope === "facility"
                ? "Facility rankings"
                : "Global rankings"}
            </Typography>
            {mine ? <YourRankCard entry={mine} /> : null}
            {page.items.length ? (
              <LeaderboardTable rows={page.items} myUserId={user?.uid} />
            ) : (
              <Typography>No players ranked yet.</Typography>
            )}
            {page.offset > 0 || page.offset + page.limit < page.total ? (
              <Stack direction="row" sx={{ justifyContent: "space-between" }}>
                <Button
                  disabled={page.offset === 0}
                  onClick={() => navigate(Math.max(0, page.offset - page.limit))}
                >
                  Previous
                </Button>
                <Button
                  disabled={page.offset + page.limit >= page.total}
                  onClick={() => navigate(page.offset + page.limit)}
                >
                  Next
                </Button>
              </Stack>
            ) : null}
          </>
        )}
      </Stack>
    </Page>
  );
}
